/**
 * useDragPlacementConfirm.ts
 * Warn-and-confirm orchestration for the Scenario-Planning machine-grid drag.
 *
 * The grid hands every drop to `request(booking, patch)`. We optimistically move
 * the bar, then persist via `updateBookingPlacement(..., { check: true })`. If the
 * backend flags a soft barrier (overlap and/or weekend/holiday start) it throws
 * `PlacementNeedsConfirmError`; we surface a PlacementConfirmDialog driven by the
 * returned `pending` state. The user then:
 *   • Book anyway  → re-PUT with { force: true }.
 *   • Use earliest → re-PUT at the earliest free start, preserving the span.
 *   • Cancel       → revert the optimistic move.
 *
 * Extracted from Phase1MachineGrid so that file stays under the 600-line cap and
 * the drag-commit logic is unit-reasoned in one place.
 */

import { useCallback, useState } from 'react';
import {
  updateBookingPlacement,
  PlacementNeedsConfirmError,
} from '../../../../services/scheduleBookings';
import type { MachineBookingCalendar } from '../../../../types/schedule';
import type { BookingPlacementPatch } from '../../../../hooks/useScheduleData';
import { expandPrepPatch, type PrepPatchPart } from '../../../../utils/prepBookingMerge';

// ---------------------------------------------------------------------------
// Date helpers (pure, UTC) — keep the span when snapping to "earliest free".
// ---------------------------------------------------------------------------

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingPlacement {
  booking: MachineBookingCalendar;
  patch: BookingPlacementPatch;
  conflict: boolean;
  weekend: boolean;
  earliestFree: string;
  conflictLabel?: string | null;
  /**
   * BUG-2026-0193 — the real bookings behind the dragged bar (one part for a
   * plain bar; every member for a merged prep bar, primary first). The primary
   * is the one the gate evaluated; siblings persist with force afterwards.
   */
  parts: PrepPatchPart[];
}

/**
 * Persist the sibling parts of a merged prep bar with `force` — the day-range
 * gate would flag each one as "overlapping" the primary that just landed on the
 * same day (that is exactly the by-design minute-packing the merge hides), and
 * the user already confirmed / the primary already passed the check.
 */
async function persistSiblings(parts: PrepPatchPart[]): Promise<void> {
  for (const part of parts.slice(1)) {
    if (Object.keys(part.patch).length === 0) continue;
    await updateBookingPlacement(part.booking.MachineBookingKey, part.patch, { force: true });
  }
}

/** Signed whole-day delta from `from` to `to` ('YYYY-MM-DD'). */
function signedDayDelta(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Shift every part's dates by the same amount so the PRIMARY starts on
 * `newStart` — each part keeps its own span (the "use earliest free" snap).
 */
function snapPartsTo(parts: PrepPatchPart[], newStart: string): PrepPatchPart[] {
  const primary = parts[0];
  const baseStart = primary.patch.StartDate ?? primary.booking.StartDate;
  const shift = signedDayDelta(baseStart, newStart);
  return parts.map(({ booking, patch }) => {
    const s = patch.StartDate ?? booking.StartDate;
    const e = patch.EndDate ?? booking.EndDate;
    return { booking, patch: { ...patch, StartDate: addDays(s, shift), EndDate: addDays(e, shift) } };
  });
}

interface UseDragPlacementConfirmOptions {
  /** Optimistically reflect a patch on the bar before the server responds. */
  applyOptimistic: (booking: MachineBookingCalendar, patch: BookingPlacementPatch) => void;
  /** Re-pull from the server (reverts optimistic move on cancel/error). */
  revert: () => void;
  /** Called after a successful persist (refetch + reload top table). */
  onCommitted: () => void;
  /** Surface an error message. */
  onError: (msg: string) => void;
}

export interface DragPlacementConfirm {
  pending: PendingPlacement | null;
  saving: boolean;
  /** Grid `onCommitPlacement` handler. */
  request: (booking: MachineBookingCalendar, patch: BookingPlacementPatch) => Promise<void>;
  /** Book anyway. */
  confirm: () => Promise<void>;
  /** Snap to the earliest free start instead. */
  useEarliest: () => Promise<void>;
  /** Dismiss + revert. */
  cancel: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDragPlacementConfirm({
  applyOptimistic,
  revert,
  onCommitted,
  onError,
}: UseDragPlacementConfirmOptions): DragPlacementConfirm {
  const [pending, setPending] = useState<PendingPlacement | null>(null);
  const [saving, setSaving] = useState(false);

  const request = useCallback(
    async (booking: MachineBookingCalendar, patch: BookingPlacementPatch) => {
      if (Object.keys(patch).length === 0) return;
      // BUG-2026-0193: a merged prep bar expands to one part per member (primary
      // first); a plain bar is its own single part. Optimistic on every member.
      const parts = expandPrepPatch(booking, patch);
      for (const part of parts) applyOptimistic(part.booking, part.patch);
      const primary = parts[0];
      try {
        await updateBookingPlacement(primary.booking.MachineBookingKey, primary.patch, { check: true });
        await persistSiblings(parts);
        onCommitted();
      } catch (err) {
        if (err instanceof PlacementNeedsConfirmError) {
          setPending({ booking, patch, parts, ...err.info });
          return; // hold the optimistic bar in place; await the user's choice
        }
        onError(err instanceof Error ? err.message : 'Failed to reschedule the booking.');
        revert();
      }
    },
    [applyOptimistic, revert, onCommitted, onError],
  );

  const confirm = useCallback(async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const primary = pending.parts[0];
      await updateBookingPlacement(primary.booking.MachineBookingKey, primary.patch, { force: true });
      await persistSiblings(pending.parts);
      onCommitted();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to reschedule the booking.');
      revert();
    } finally {
      setSaving(false);
      setPending(null);
    }
  }, [pending, onCommitted, onError, revert]);

  const useEarliest = useCallback(async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const { earliestFree } = pending;
      // Preserve the dragged span(s): every part shifts by the same delta so the
      // primary lands on earliestFree (a plain bar: prefer the patch's own dates,
      // else the booking's current span — unchanged behaviour).
      const snapped = snapPartsTo(pending.parts, earliestFree);
      for (const part of snapped) applyOptimistic(part.booking, part.patch);
      // earliestFree is a free working day, so force-persist (no second prompt).
      const primary = snapped[0];
      await updateBookingPlacement(primary.booking.MachineBookingKey, primary.patch, { force: true });
      await persistSiblings(snapped);
      onCommitted();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to reschedule the booking.');
      revert();
    } finally {
      setSaving(false);
      setPending(null);
    }
  }, [pending, applyOptimistic, onCommitted, onError, revert]);

  const cancel = useCallback(() => {
    setPending(null);
    revert();
  }, [revert]);

  return { pending, saving, request, confirm, useEarliest, cancel };
}
